import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  useMediaQuery,
  CardHeader,
  IconButton,
  Collapse,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Search,
  Inventory,
  AttachMoney,
  Warning,
  TrendingUp,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  BarChart,

  Bar,
} from "recharts";
import CountUp from "react-countup";
import { getMainDashboardCounts } from "../../../Services/Services";
import { toast } from "react-toastify";
import InfiniteScroll from "react-infinite-scroll-component";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
const COLORS = ["#3f51b5", "#f50057", "#ff9800", "#4caf50", "#9c27b0"];

const DashboardPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
const [sizeWiseStock, setSizeWiseStock] = useState([]);


  const [expandedItem, setExpandedItem] = useState(null);

  // Fetch data from API
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await getMainDashboardCounts();
      // response is already the JSON object you showed
      setDashboardData(response);
      // Set size-wise stock data
      setSizeWiseStock(response.size_wise_stock || []);
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Failed to load dashboard data");
      // Optionally set fallback data or keep null
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Format currency
  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);

  // If still loading
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  // If no data or error
  if (!dashboardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">No data available. Please check API connection.</Typography>
      </Box>
    );
  }

  // Destructure data with fallbacks
  const {
    summary = {},
    stockByCategory = [],
    stockStatus = {},
    monthlyTrend = [],
    items = [],
   
  } = dashboardData;

  // Filter items based on search (name or SKU)
  const filteredItems = items.filter(
    (item) =>
      (item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.sku && item.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // KPI cards data from API summary
  const kpiCards = [
    {
      title: "Total Items",
      value: summary.totalItems || 0,
      sub: "All Items",
      icon: <Inventory sx={{ fontSize: 32 }} />,
      gradient: "linear-gradient(135deg, #1976d2, #64b5f6)",
    },
    {
      title: "Total Stock (Qty)",
      value: summary.totalStockQty || 0,
      sub: "All Warehouses",
      icon: <Inventory sx={{ fontSize: 32 }} />,
      gradient: "linear-gradient(135deg, #2e7d32, #81c784)",
    },
    {
      title: "Total Inventory Value",
      value: summary.totalInventoryValue || 0,
      sub: "All Items Value",
      icon: <AttachMoney sx={{ fontSize: 32 }} />,
      gradient: "linear-gradient(135deg, #ed6c02, #ffb74d)",
      isCurrency: true,
    },
    {
      title: "Low Stock Items",
      value: summary.lowStockItems || 0,
      sub: "Check Items",
      icon: <Warning sx={{ fontSize: 32 }} />,
      gradient: "linear-gradient(135deg, #d32f2f, #ef9a9a)",
    },
  ];

  return (
   <Box sx={{ mt: { xs: 5, md: 0 }, minHeight: "100vh" }}>

      {/* KPI Cards Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {kpiCards.map((card, idx) => (
          <Grid item xs={12} sm={6} md={3} key={idx}>
            <Card
              sx={{
                background: card.gradient,
                color: "white",
                borderRadius: 4,
                transition: "transform 0.25s ease, box-shadow 0.25s ease",
                "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 24px -8px rgba(0,0,0,0.2)" },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.85, fontWeight: 500 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" sx={{ lineHeight: 1.2, mt: 0.5 }}>
                      <CountUp
                        end={card.value}
                        duration={1.5}
                        separator=","
                        prefix={card.isCurrency ? "₹" : ""}
                      />
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.75, fontWeight: 500 }}>
                      {card.sub}
                    </Typography>
                  </Box>
                  {card.icon}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Secondary metrics: Raw Material & Finished Goods Value */}
     <Grid container spacing={3} sx={{ mb: 4 }}>
  {sizeWiseStock?.map((item) => (
    <Grid item xs={12} sm={6} md={6} key={item.id}>
      <Card
        sx={{
          borderRadius: 4,
          height: "100%",
          boxShadow: 2,
        }}
      >
        <CardContent>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            gutterBottom
          >
            {item.size} Material Value
          </Typography>

          <Typography
            variant="h5"
            fontWeight="bold"
            color="#2e7d32"
          >
            ₹{formatCurrency(item.total_stock_value)}
          </Typography>

          <Typography variant="caption" color="text.secondary">
            Total Stock : {item.total_stock}
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  ))}
</Grid>

      {/* Charts Row */}
    {/* Stock Value by Category - Horizontal Scrollable Bar Chart */}
<Grid item xs={12} md={5}>
  <Card sx={{ p: 2, borderRadius: 4, boxShadow: 2, height: "100%", overflow: "hidden" }}>
    <Typography variant="h6" fontWeight="600" gutterBottom>
      Stock Value by Category
    </Typography>
    {stockByCategory.length > 0 ? (
      <Box sx={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
        <Box sx={{ minWidth: Math.max(stockByCategory.length * 120, 300), height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={stockByCategory}
              margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
                tick={{ fontSize: 11 }}
              />
              <YAxis
                tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
              />
              <Tooltip
                formatter={(value) => `₹${formatCurrency(value)}`}
                labelFormatter={(label) => `Category: ${label}`}
              />Inventory Items
              <Bar
                dataKey="value"
                fill="#3f51b5"
                radius={[6, 6, 0, 0]}
                barSize={50}
              >
                {stockByCategory.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Box>
    ) : (
      <Typography sx={{ textAlign: "center", py: 10 }} color="text.secondary">
        No category data available
      </Typography>
    )}
  </Card>
</Grid>

      {/* Items Table with Search & Pagination */}
<Card sx={{ boxShadow: 3, mt: 2 }}>
  <CardHeader
    title="Top Low Stock Items"
    titleTypographyProps={{ variant: "h6", fontWeight: "bold", color: "black" }}
    sx={{ pb: 0 }}
  />
  <CardContent>
    <Box id="items-table-print">
      <InfiniteScroll
        dataLength={items.length}          // ✅ use items, not sizeWiseStock
        // next={fetchMoreData}
        // hasMore={hasMore}
        loader={<Box sx={{ textAlign: "center", p: 2 }}><Typography>Loading...</Typography></Box>}
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW - Dropdown Cards */
          <Box>
            {items.map((row, index) => (    // ✅ use items
              <Box key={index}>
                {/* Header Card */}
                <Box
                  onClick={() => setExpandedItem(expandedItem === index ? null : index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:active": { bgcolor: "#e3f2fd" },
                    borderLeft: "4px solid #3498db",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.3 }}>
                      <Typography sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}>
                        #{index + 1}
                      </Typography>
                      <Typography sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}>
                        {row.name ||  "N/A"}   {/* fallback */}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "#666", display: "flex", alignItems: "center", gap: 1 }}>
                      <span style={{ fontWeight: 500 }}>
                        {row.category_name?.substring(0, 25) || "N/A"}
                        {row.category_name?.length > 25 ? "..." : ""}
                      </span>
                    </Typography>
                  </Box>
                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedItem === index ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </IconButton>
                </Box>

                {/* Expanded Details */}
                <Collapse in={expandedItem === index} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, py: 1.5, bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa", borderLeft: "4px solid #3498db" }}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.2 }}>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>
                          Product Type
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#3498db" }}>
                          {row.size_name || row.sku || "-"}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>
                          STOCK QTY
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#2ecc71" }}>
                          {row.current_stock}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: "1 / -1" }}>
                        <Typography sx={{ fontSize: "0.65rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>
                          Max Qty
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#333" }}>
                          {row.max_quantity}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>
                          MIN QTY
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#e74c3c" }}>
                          {row.min_quantity}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "0.65rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>
                          Unit
                        </Typography>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#333" }}>
                          {row.unit_name || "-"}
                        </Typography>
                       
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
                <Divider />
              </Box>
            ))}
          </Box>
        ) : (
          /* 💻 DESKTOP VIEW - Table */
          <TableContainer component={Paper} sx={{ maxHeight: "53vh", overflowX: "auto", border: "1px solid #e0e0e0", borderRadius: 0 }}>
            <Table size="small" stickyHeader sx={{ minWidth: 1000, "& td, & th": { textAlign: "center", whiteSpace: "nowrap", borderBottom: "1px solid #ccc" } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5", "& th": { fontWeight: "bold", color: "#333", borderBottom: "1px solid #999", backgroundColor: "#f5f5f5" } }}>
                  <TableCell sx={{ padding: "0px" }}>S.No</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Product Type</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Stock Qty</TableCell>
                  <TableCell>Minimum Qty</TableCell>
                  <TableCell>Maximum Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row, index) => (   // ✅ use items
                  <TableRow
                    hover
                    key={index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "& td": { paddingTop: "2px !important", paddingBottom: "2px !important", fontSize: { xs: "0.75rem", sm: "0.8125rem" }, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 },
                      "&:hover": { backgroundColor: "#e3f2fd !important" },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.category_name}</TableCell>
                    <TableCell>{row.size_name}</TableCell>
                    <TableCell>{row.unit_name}</TableCell>
                    <TableCell>{row.current_stock}</TableCell>
                    <TableCell>{row.min_quantity}</TableCell>
                    <TableCell>{row.max_quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>
    </Box>
  </CardContent>
</Card>
    </Box>
  );
};

export default DashboardPage;